import React from 'react';
import { faSearch } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useSearchAutoComplete from '@v2/shared/lib/useSearchAutoComplete';
import SearchSuggestionsPanel from '@v2/shared/components/SearchSuggestionsPanel';
import { Item } from '@v2/shared/lib/useAutoComplete';

type Props = {
  /**
   * The input's id, which the listbox and `aria-controls` are derived from. Named by the
   * caller rather than defaulted here, for the reason SearchForm gives: only the caller
   * knows how many searches are on the page, and a duplicate id quietly breaks the aria
   * wiring on every one of them.
   */
  id: string;
};

/**
 * The search box in the header's second strip.
 *
 * headless: apps/topmags/layouts/MainLayout/Navigation/Search, which is where the markup
 * came from. MagSpot's and MagZoom's legacy headers mount `Components.Search` — the
 * DMagzWeb one — with `searchPath: '/search?xSearch='`, which is the same suggestions api
 * this hook already talks to and the same box drawn around it, so the port arrived at one
 * component from two directions.
 *
 * headless routes the selection with next/router; there is no router here, so it does
 * what DiscountMags' NavigationMount does and assigns location. The fallback url is the
 * legacy search path — what `buildSearchUrl` resolved to there, and what all three legacy
 * headers pass as their `searchPath`.
 *
 * The placeholder is fixed rather than a prop because all three headers say the same
 * thing, word for word. A brand that wants its own can have one when it asks.
 *
 * The type is text-base, and that is a constraint rather than a preference: iOS Safari
 * zooms the page whenever a focused input is under 16px, so anything smaller means that
 * tapping search jerks the viewport on every iPhone. text-xs and then text-sm, which this
 * box has worn in turn, both trip it; DiscountMags' search has always been text-base,
 * which is why it never did.
 *
 * The field stays 42px, which is what that search measures. Its height is entirely this
 * wrapper's padding -- preflight zeroes an input's own -- so it is the padding plus the
 * 24px line-box of text-base plus the border, which makes it py-2. The same 42px was
 * py-2.5 against text-sm and py-3 against text-xs: the padding has to move whenever the
 * type does, so change the two together or the box changes height behind you.
 */
export default function Search({ id }: Props) {
  const {
    query,
    open,
    items,
    isSelected,
    onChange,
    onBlur,
    onFocus,
    onKeyDown,
    onClick,
    inputProps,
    itemsContainerProps,
    isSpacer,
  } = useSearchAutoComplete((item: Item) => {
    window.location.href = item.url || `/search?xSearch=${encodeURIComponent(item.title)}`;
  });

  return (
    <div className="relative flex w-full items-center border border-solid border-slate-200 bg-white px-3 py-2">
      <input
        id={id}
        value={query}
        type="text"
        placeholder="Search for magazines"
        className="mr-3 w-full text-base"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        aria-controls={`${id}-listbox`}
        onBlur={onBlur}
        onFocus={onFocus}
        onChange={onChange}
        onKeyDown={onKeyDown}
        {...inputProps}
      />
      <FontAwesomeIcon icon={faSearch} className="cursor-pointer text-slate-500" onClick={() => onClick()} />
      <SearchSuggestionsPanel
        open={open}
        listboxId={`${id}-listbox`}
        itemsContainerProps={itemsContainerProps}
        items={items}
        onClick={onClick}
        isSelected={isSelected}
        isSpacer={isSpacer}
      />
    </div>
  );
}
